import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BookmarkIcon, PlusIcon, ChartBarIcon, ArrowPathIcon, FolderIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import BookmarkCard from '../components/BookmarkCard';
import ErrorMessage from '../components/ErrorMessage';
import { bookmarkService } from '../services/bookmarkService';

const Dashboard = () => {
  const [recentBookmarks, setRecentBookmarks] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    processed: 0,
    processing: 0,
    failed: 0,
    collections: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    loadDashboardData();

    // Set up auto-refresh every 30 seconds to catch processing updates
    intervalRef.current = setInterval(() => {
      loadDashboardData(true); // Silent refresh
    }, 30000);

    // Reload when the user returns to the tab after idle (e.g. next morning).
    // App.js already refreshes auth on visibilitychange, so tokens will be
    // fresh by the time our fetch runs.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') loadDashboardData();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const loadDashboardData = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      } else {
        setRefreshing(true);
      }

      // Fetch recent bookmarks, stats, and collections in parallel
      // Use getUserStats for accurate counts — avoids the 100-item API cap
      const [bookmarksResponse, userStats, collectionsData] = await Promise.all([
        bookmarkService.getBookmarks({ limit: 6 }),
        bookmarkService.getUserStats().catch(() => null),
        bookmarkService.listCollections().catch(() => ({ items: [] })),
      ]);

      setRecentBookmarks(bookmarksResponse.items || []);

      setStats({
        total: userStats?.bookmarkCount ?? 0,
        processed: userStats?.processedCount ?? 0,
        processing: userStats?.processingCount ?? 0,
        failed: userStats?.failedCount ?? 0,
        collections: collectionsData?.items?.length ?? 0,
      });
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Suppress errors during background interval refreshes — a transient network
      // blip (e.g. computer waking from sleep before the network is ready) should
      // not replace working dashboard content with an error banner.
      if (!silent) setError(error.message || 'Failed to load dashboard data');
    } finally {
      if (!silent) setLoading(false);
      else setRefreshing(false);
    }
  };

  const handleRefresh = () => loadDashboardData();

  const handleUpdateBookmark = async (bookmarkId, updateData) => {
    try {
      const updated = await bookmarkService.updateBookmark(bookmarkId, updateData);
      setRecentBookmarks(prev => prev.map(b => b.bookmarkId === bookmarkId ? updated : b));
    } catch (error) {
      console.error('Error updating bookmark:', error);
      setError(error.message || 'Failed to update bookmark');
    }
  };

  const handleRetryBookmark = async (bookmarkId) => {
    try {
      await bookmarkService.retryBookmark(bookmarkId);
      setRecentBookmarks(prev => prev.map(b =>
        b.bookmarkId === bookmarkId ? { ...b, status: 'processing' } : b
      ));
      setStats(prev => ({ ...prev, failed: prev.failed - 1, processing: prev.processing + 1 }));
    } catch (error) {
      console.error('Error retrying bookmark:', error);
      setError(error.message || 'Failed to retry bookmark');
    }
  };

  const handleDeleteBookmark = async (bookmarkId) => {
    try {
      await bookmarkService.deleteBookmark(bookmarkId);
      setRecentBookmarks(prev => prev.filter(b => b.bookmarkId !== bookmarkId));
      setStats(prev => ({ ...prev, total: prev.total - 1 }));
    } catch (error) {
      console.error('Error deleting bookmark:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {error && (
        <ErrorMessage
          message={error}
          onRetry={() => loadDashboardData()}
        />
      )}

      {/* Welcome */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-start mb-2">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {stats.total === 0 ? 'Welcome to BookMarker' : 'Your BookMarker Dashboard'}
          </h1>
          <button
            onClick={handleRefresh}
            disabled={loading || refreshing}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50"
            title={lastUpdated ? `Last updated: ${lastUpdated.toLocaleTimeString()}` : 'Click to refresh'}
          >
            <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {stats.total === 0
            ? 'Save, organize, and search your web bookmarks with AI-powered content analysis.'
            : `You have ${stats.total} bookmark${stats.total !== 1 ? 's' : ''}${stats.collections > 0 ? ` across ${stats.collections} collection${stats.collections !== 1 ? 's' : ''}` : ''}. ${stats.processing > 0 ? `${stats.processing} ${stats.processing === 1 ? 'is' : 'are'} still being processed.` : ''}`
          }
        </p>
        <Link to="/add" className="btn-primary inline-flex items-center space-x-2">
          <PlusIcon className="h-5 w-5" />
          <span>{stats.total === 0 ? 'Add Your First Bookmark' : 'Add Another Bookmark'}</span>
        </Link>
      </div>

      {/* Stats — always 4 cards; failed shown as alert below */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <Link to="/bookmarks" className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <BookmarkIcon className="h-8 w-8 text-primary-600 flex-shrink-0" />
            <div className="ml-4 min-w-0">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Bookmarks</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</p>
            </div>
          </div>
        </Link>

        <Link to="/collections" className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <FolderIcon className="h-8 w-8 text-indigo-500 flex-shrink-0" />
            <div className="ml-4 min-w-0">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Collections</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.collections}</p>
            </div>
          </div>
        </Link>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <ChartBarIcon className="h-8 w-8 text-green-600 flex-shrink-0" />
            <div className="ml-4 min-w-0">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Processed</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.processed}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center flex-shrink-0">
              <div className={`h-4 w-4 bg-yellow-600 rounded-full ${stats.processing > 0 ? 'animate-pulse' : ''}`} />
            </div>
            <div className="ml-4 min-w-0">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Processing</p>
              <div className="flex items-center space-x-2">
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.processing}</p>
                {stats.processing > 0 && (
                  <span className="text-xs text-yellow-600 font-medium">Active</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Failed alert — only shown when there are failures */}
      {stats.failed > 0 && (
        <div className="flex items-center justify-between bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-5 py-3">
          <div className="flex items-center gap-3">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0" />
            <p className="text-sm font-medium text-red-800 dark:text-red-400">
              {stats.failed} bookmark{stats.failed !== 1 ? 's' : ''} failed to process
            </p>
          </div>
          <Link
            to="/broken-links"
            className="text-sm font-medium text-red-700 dark:text-red-400 hover:text-red-900 transition-colors whitespace-nowrap"
          >
            Manage →
          </Link>
        </div>
      )}

      {/* Recent Bookmarks */}
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Recent Bookmarks</h2>
          <Link to="/bookmarks" className="text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium">
            View All →
          </Link>
        </div>

        {recentBookmarks.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
            <BookmarkIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No bookmarks yet</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Start building your bookmark collection by adding your first URL.
            </p>
            <Link to="/add" className="btn-primary">Add Bookmark</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {recentBookmarks.map((bookmark) => (
              <BookmarkCard
                key={bookmark.bookmarkId}
                bookmark={bookmark}
                onDelete={handleDeleteBookmark}
                onUpdate={handleUpdateBookmark}
                onRetry={handleRetryBookmark}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
